import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { Grid, Theme, Typography, Card, Box, useMediaQuery } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  Loader,
  Page,
  Button,
  Table,
  Link,
  PageTitleWithButton,
  ItemValue
} from '../../../components';
import { getPurchaseOrdersDetail } from '../../../fetch';
import { IPurchaseOrderDetails, IPOPart } from '../../../models';
import { formatAddress, formatDate, formatMoney } from '../../../helpers';
import ReactToPrint from 'react-to-print';
import { Print } from '@mui/icons-material';

interface IPurchaseOrdersDetails {}

export const PurchaseOrdersDetails: FC<IPurchaseOrdersDetails> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingPurchaseOrderDetails, setIsLoadingPurchaseOrderDetails] = useState<boolean>(true);
  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState<IPurchaseOrderDetails>(null);
  const printRef = useRef();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { purchaseOrderNumber }: { purchaseOrderNumber: string } = useParams();
  const classes = useStyles();

  const fetchPurchaseOrderDetails = async () => {
    try {
      setIsLoadingPurchaseOrderDetails(true);
      const res = await getPurchaseOrdersDetail(purchaseOrderNumber);
      if (res?.purchaseOrderNumber) {
        setPurchaseOrderDetails(res);
      } else if (res?.Detail) {
        enqueueSnackbar(res?.Detail, {
          variant: 'error'
        });
      } else {
        enqueueSnackbar(`Something went wrong. please refresh and try again`, {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Something went wrong. ${error}`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingPurchaseOrderDetails(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partColumns = useMemo(() => {
    return [
      {
        Header: 'Part ID',
        accessor: 'partId',
        sort: false
      },
      {
        Header: 'Part',
        accessor: 'part',
        sort: false
      },
      {
        Header: 'Price',
        accessor: 'price',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPOPart } };
        }) => {
          return <span>{formatMoney(original.price)}</span>;
        }
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
        sort: false
      },
      {
        Header: 'Total',
        accessor: 'total',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPOPart } };
        }) => {
          return <span>{formatMoney(original.total)}</span>;
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderDetails]);

  return (
    <Page
      title={`Purchase Order Details${
        purchaseOrderDetails?.purchaseOrderNumber
          ? ` for ${purchaseOrderDetails?.purchaseOrderNumber}`
          : ''
      }`}
    >
      {purchaseOrderDetails && (
        <PageTitleWithButton
          button={
            <ReactToPrint
              trigger={() => (
                <Button
                  id="Print"
                  startIcon={<Print />}
                  type="button"
                  color="primary"
                  variant="contained"
                >
                  Print
                </Button>
              )}
              content={() => printRef.current}
            />
          }
          pageTitleBackProps={{
            backText: 'Back to Purchase Orders',
            backUrl: '/vendors/purchase-orders',
            title: `Purchase Order${
              purchaseOrderDetails?.purchaseOrderNumber
                ? ` - ${purchaseOrderDetails?.purchaseOrderNumber}`
                : ''
            }`
          }}
        />
      )}
      <Box display="flex" justifyContent="center" position="relative">
        {isLoadingPurchaseOrderDetails && <Loader type="overlay" position="centered" />}
        {!isLoadingPurchaseOrderDetails && !purchaseOrderDetails && (
          <Box
            display="flex"
            height="10rem"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Typography>Purchase order does not exist</Typography>
            <Link to="/vendors/purchase-orders">Back to purchase orders</Link>
          </Box>
        )}
        {purchaseOrderDetails && (
          <>
            <Grid ref={printRef} item xs={12} lg={9}>
              <Card variant={'outlined'} className={classes.card}>
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <ItemValue
                      label="Purchase Order Number"
                      value={purchaseOrderDetails.purchaseOrderNumber}
                    />
                    <ItemValue
                      label="Order Date"
                      value={formatDate(purchaseOrderDetails.orderDate)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ItemValue label="Status" value={purchaseOrderDetails.status} />
                    <ItemValue label="Job" value={purchaseOrderDetails.job} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ItemValue label="Partner" value={purchaseOrderDetails.partner} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ItemValue label="Description" value={purchaseOrderDetails.description} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ItemValue label="Order Type" value={purchaseOrderDetails.orderType} />
                    {/* shove the notes field flush under order type on desktop */}
                    {!isMobile && <ItemValue label="Notes" value={purchaseOrderDetails.notes} />}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ItemValue
                      label="Address"
                      value={formatAddress({
                        address1: purchaseOrderDetails.address1,
                        address2: purchaseOrderDetails.address2
                      })}
                    />
                    <ItemValue
                      label=""
                      value={formatAddress({
                        city: purchaseOrderDetails.city,
                        state: purchaseOrderDetails.state,
                        zip: purchaseOrderDetails.zip
                      })}
                    />
                  </Grid>
                  {/* shove the notes field flush under address on mobile */}
                  {isMobile && (
                    <Grid item xs={12}>
                      <ItemValue label="Notes" value={purchaseOrderDetails.notes} />
                    </Grid>
                  )}
                  <Grid className={classes.lineItems} item xs={12}>
                    <>
                      <Table
                        columns={partColumns}
                        data={purchaseOrderDetails.parts}
                        isLoading={isLoadingPurchaseOrderDetails}
                        hidePagination
                      />
                      <div className={classes.total}>
                        <ItemValue label="Tax" value={formatMoney(purchaseOrderDetails.tax)} />
                        <ItemValue
                          label="Subtotal"
                          value={formatMoney(purchaseOrderDetails.subtotal)}
                        />
                        <ItemValue label="Total" value={formatMoney(purchaseOrderDetails.total)} />
                      </div>
                    </>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </>
        )}
      </Box>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    padding: theme.spacing(1)
  },
  lineItems: {
    marginTop: theme.spacing(1)
  },
  total: {
    padding: theme.spacing(1),
    textAlign: 'right'
  }
}));
